import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Layout } from 'lucide-react';
import { useOffices } from '../../offices';
import '../../assets/styles/unified-controls.css';

const DashboardFilters = ({
  filtersOpen,
  setFiltersOpen,
  periodType,
  setPeriodType,
  periodValue,
  setPeriodValue,
  office,
  setOffice,
  getPeriodOptions
}) => {
  const theme = useTheme();
  const { offices } = useOffices();
  return (
    <Card sx={{
      mb: 4,
      boxShadow: theme.shadows[6],
      borderRadius: 4,
      //   background: `linear-gradient(135deg, ${theme.palette.primary.main} 50%, ${theme.palette.primary.dark} 100%)`,
      color: 'theme.palette.primary.dark',
      overflow: 'hidden',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="10" cy="10" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        opacity: 0.3
      }
    }}>
      <CardContent sx={{ p: { xs: 2, md: 4 }, position: 'relative', zIndex: 1 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 }
        }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{
              p: 1.5,
              bgcolor: '#b5caffff',
              borderRadius: 2,
              backdropFilter: 'blur(10px)'
            }}>
              {/* <Layout size={24} color={theme.palette.primary.dark} /> */}
              <Layout size={24} color='#3d52a0' />
            </Box>
            <Box>
              <Typography variant="h5" sx={(theme) => ({ fontWeight: 700, mb: 0.5, color: '#3d52a0' })}>
                Dashboard Controls
              </Typography>
              <Typography variant="body2" sx={(theme) => ({ color: '#3d52a0' })}>
                Filter and customize your dashboard view
              </Typography>
            </Box>
          </Stack>
          <Grid container spacing={3} sx={{ ml: 'auto' }}>
            <Grid item xs={12} sm={6} md={4}>
              <Tooltip title="Select the type of period to filter data" placement="top">
                <select
                  className="axo-filter-select"
                  style={{ width: '100%' }}
                  value={periodType}
                  onChange={(e) => {
                    setPeriodType(e.target.value);
                    if (e.target.value === 'month') {
                      setPeriodValue(`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`);
                    } else if (e.target.value === 'week') {
                      setPeriodValue(`${new Date().getFullYear()}-W${String(Math.ceil(new Date().getDate() / 7)).padStart(2, '0')}`);
                    } else {
                      setPeriodValue(`${new Date().getFullYear()}`);
                    }
                  }}
                >
                  <option value="month">Month</option>
                  <option value="year">Year</option>
                </select>
              </Tooltip>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Tooltip title="Select a specific period" placement="top">
                <select
                  className="axo-filter-select"
                  style={{ width: '100%' }}
                  value={periodValue}
                  onChange={(e) => setPeriodValue(e.target.value)}
                >
                  {getPeriodOptions().map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Tooltip>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Tooltip title="Filter data by office location" placement="top">
                <select
                  className="axo-filter-select"
                  style={{ width: '100%' }}
                  value={office}
                  onChange={(e) => setOffice(e.target.value)}
                >
                  <option value="All">All Offices</option>
                  {offices.map(off => (
                    <option key={off.id} value={off.name}>{off.name}</option>
                  ))}
                </select>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
        {/* {isMobile && (
            <Button
              onClick={() => setFiltersOpen(!filtersOpen)}
              startIcon={<FilterListIcon />}
              variant="outlined"
              sx={{ 
                color: 'white',
                borderColor: 'rgba(255,255,255,0.3)',
                bgcolor: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.6)',
                  bgcolor: 'rgba(255,255,255,0.2)'
                }
              }}
            >
              {filtersOpen ? 'Hide Filters' : 'Show Filters'}
            </Button>
          )}
        </Box>
        
        <Collapse in={filtersOpen}>
          <Divider sx={{ mb: 3, borderColor: 'rgba(255,255,255,0.2)' }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <Tooltip title="Select the type of period to filter data" placement="top">
                <FormControl fullWidth>
                  <InputLabel sx={{ color: 'rgba(255,255,255,0.8)' }}>Period Type</InputLabel>
                  <Select
                    value={periodType}
                    label="Period Type"
                    onChange={(e) => {
                      setPeriodType(e.target.value);
                      if (e.target.value === 'month') {
                        setPeriodValue(`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`);
                      } else if (e.target.value === 'week') {
                        setPeriodValue(`${new Date().getFullYear()}-W${String(Math.ceil(new Date().getDate() / 7)).padStart(2, '0')}`);
                      } else {
                        setPeriodValue(`${new Date().getFullYear()}`);
                      }
                    }}
                    sx={{
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.6)' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                      '& .MuiSvgIcon-root': { color: 'white' }
                    }}
                  >
                    {/* <MenuItem value="week">Per Week</MenuItem>
                    <MenuItem value="month">Month</MenuItem>
                    <MenuItem value="year">Year</MenuItem>
                  </Select>
                </FormControl>
              </Tooltip>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <Tooltip title="Select a specific period" placement="top">
                <FormControl fullWidth>
                  <InputLabel sx={{ color: 'rgba(255,255,255,0.8)' }}>Period</InputLabel>
                  <Select
                    value={periodValue}
                    label="Period"
                    onChange={(e) => setPeriodValue(e.target.value)}
                    sx={{
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.6)' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                      '& .MuiSvgIcon-root': { color: 'white' }
                    }}
                  >
                    {getPeriodOptions().map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Tooltip>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <Tooltip title="Filter data by office location" placement="top">
                <FormControl fullWidth>
                  <InputLabel sx={{ color: 'rgba(255,255,255,0.8)' }}>Office</InputLabel>
                  <Select
                    value={office}
                    label="Office"
                    onChange={(e) => setOffice(e.target.value)}
                    sx={{
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.6)' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                      '& .MuiSvgIcon-root': { color: 'white' }
                    }}
                  >
                    <MenuItem value="All">All Offices</MenuItem>
                    <MenuItem value="Nador">Nador</MenuItem>
                    <MenuItem value="Tangier">Tangier</MenuItem>
                  </Select>
                </FormControl>
              </Tooltip>
            </Grid>
          </Grid>
        </Collapse> */}
      </CardContent>
    </Card>
  );
};

export default DashboardFilters;